import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Banner, Button, Card, Modal, Spinner, Text, Tooltip } from '@shopify/polaris';
import { CheckCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import agent from '../../app/api/agent';
import type { ProductFitmentCopyJob, ProductVariantFitmentScope } from '../../app/models/Product';
import './ProductCopyActivity.css';

const RECENT_COPY_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const HISTORY_PAGE_SIZE = 25;
type ActivityProps = {
    jobs: ProductFitmentCopyJob[];
    variants?: ProductVariantFitmentScope[];
    disabled: boolean;
    onRetry: (job: ProductFitmentCopyJob) => Promise<ProductFitmentCopyJob | undefined>;
};

export function isRecentCopyJob(job: ProductFitmentCopyJob, now = Date.now()) {
    if (job.isActive || ['Queued', 'Processing', 'Failed', 'CompletedWithWarnings'].includes(job.state)) return true;
    const timestamp = Date.parse(job.completedAt || job.requestedAt);
    // A missing/invalid timestamp is not evidence that a result has expired.
    return !Number.isFinite(timestamp) || now - timestamp < RECENT_COPY_AGE_MS;
}

const formatTimestamp = (value: string) => Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'Date unavailable';

const relativeTimestamp = (value: string) => {
    if (!Number.isFinite(Date.parse(value))) return 'Date unavailable';
    const date = new Date(value);
    const days = Math.max(0, Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000)));
    const day = days === 0 ? 'Today' : days === 1 ? 'Yesterday' : `${days} days ago`;
    return `${day} · ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

function CopyActivityEntry({ job, variants = [], disabled, onRetry, absoluteDate = false }: Omit<ActivityProps, 'jobs'> & {
    job: ProductFitmentCopyJob; absoluteDate?: boolean;
}) {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const completed = job.state === 'Completed';
    const timestamp = job.completedAt || job.requestedAt;
    const source = job.sourceProductTitle || `Product ${job.sourceProductId}`;
    const variant = variants.find((item) => item.id === job.targetVariantId);
    const scope = job.targetVariantId ? `Variant: ${variant?.sku || variant?.title || job.targetVariantId}` : 'Product-level fitments';
    const tone = completed ? 'success' : job.state === 'Failed' ? 'critical' : job.state === 'CompletedWithWarnings' ? 'attention' : job.isActive ? 'info' : undefined;
    const stateLabel = job.state === 'CompletedWithWarnings' ? 'Completed with warnings' : job.state === 'Processing' ? 'Copying' : job.state;
    const result = completed ? `${job.totalFitments.toLocaleString()} ${job.totalFitments === 1 ? 'fitment' : 'fitments'} copied`
        : job.state === 'Failed' ? 'Copy needs attention' : job.state === 'CompletedWithWarnings' ? 'Copy completed with warnings'
            : job.state === 'Queued' ? 'Copy queued' : job.state === 'Cancelled' ? 'Copy cancelled' : 'Copying fitments';

    return (
        <article className="pf-copy-entry" aria-label={`${stateLabel} copy from ${source}`}>
            {completed && <CheckCircleIcon className="pf-copy-entry__success" width={22} height={22} aria-hidden="true" />}
            <div className="pf-copy-entry__content">
                <div className="pf-copy-entry__heading">
                    <Text as="h3" variant="headingSm">{result}</Text>
                    <Badge tone={tone}>{stateLabel}</Badge>
                </div>
                <Tooltip content={source}>
                    <p className="pf-copy-entry__source">From <span>{source}</span></p>
                </Tooltip>
                {!completed && job.message && <Text as="p" tone="subdued">{job.message}</Text>}
                <div className="pf-copy-entry__meta">
                    <Tooltip content={formatTimestamp(timestamp)}>
                        <time dateTime={Number.isFinite(Date.parse(timestamp)) ? timestamp : undefined}>
                            {absoluteDate ? formatTimestamp(timestamp) : relativeTimestamp(timestamp)}
                        </time>
                    </Tooltip>
                    {job.targetVariantId && <span>{scope}</span>}
                    <Button variant="plain" disclosure={detailsOpen ? 'up' : 'down'} onClick={() => setDetailsOpen((open) => !open)} ariaExpanded={detailsOpen}>
                        {detailsOpen ? 'Hide details' : 'Show details'}
                    </Button>
                    {job.state === 'Failed' && <Button variant="plain" disabled={disabled} onClick={() => { void onRetry(job); }}>Retry copy</Button>}
                </div>
                {detailsOpen && <dl className="pf-copy-entry__details">
                    <dt>Source product</dt><dd>{source}</dd>
                    <dt>Destination scope</dt><dd>{scope}</dd>
                    {completed && <><dt>Fitments copied</dt><dd>{job.totalFitments.toLocaleString()}</dd></>}
                    <dt>Added</dt><dd>{job.addedFitments.toLocaleString()}</dd>
                    <dt>Already present</dt><dd>{job.unchangedFitments.toLocaleString()}</dd>
                    {job.removedFitments > 0 && <><dt>Removed</dt><dd>{job.removedFitments.toLocaleString()}</dd></>}
                    <dt>Notes copied</dt><dd>{job.copiedNotes.toLocaleString()}{!job.includeNotes ? ' (not included)' : ''}</dd>
                    <dt>Started</dt><dd>{formatTimestamp(job.startedAt || job.requestedAt)}</dd>
                    {job.completedAt && <><dt>Finished</dt><dd>{formatTimestamp(job.completedAt)}</dd></>}
                </dl>}
            </div>
        </article>
    );
}

export function RecentCopyActivity({ jobs, onViewAll, ...props }: ActivityProps & { onViewAll: () => void }) {
    const [expanded, setExpanded] = useState(false);
    const recent = jobs.filter((job) => isRecentCopyJob(job));
    if (!recent.length) return null;
    return (
        <section className="pf-copy-activity" aria-label="Recent copy activity">
            <Card padding="0">
                <div className="pf-copy-activity__header">
                    <button type="button" className="pf-copy-activity__toggle" aria-expanded={expanded} onClick={() => setExpanded((open) => !open)}>
                        {expanded ? <ChevronDownIcon width={20} height={20} aria-hidden="true" /> : <ChevronRightIcon width={20} height={20} aria-hidden="true" />}
                        <Text as="span" variant="headingSm">Recent copy activity</Text>
                        <Badge>{String(recent.length)}</Badge>
                    </button>
                    <Button variant="plain" onClick={onViewAll}>View all</Button>
                </div>
                {expanded && <div className="pf-copy-activity__entries">
                    {recent.map((job) => <CopyActivityEntry key={job.id} job={job} {...props} />)}
                </div>}
            </Card>
        </section>
    );
}

export function CopyHistoryModal({ productId, open, onClose, jobs, onRetry, ...props }: ActivityProps & {
    productId: number; open: boolean; onClose: () => void;
}) {
    const [history, setHistory] = useState<ProductFitmentCopyJob[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasMore, setHasMore] = useState(false);
    const abortRef = useRef<AbortController>();
    const lastCursor = useRef<number>();
    const load = useCallback(async (beforeId?: number) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        lastCursor.current = beforeId;
        setLoading(true);
        setError('');
        try {
            const page = await agent.Products.listFitmentCopyJobs(productId, HISTORY_PAGE_SIZE, { beforeId, signal: controller.signal });
            if (controller.signal.aborted) return;
            setHistory((current) => beforeId
                ? [...current, ...page.filter((job) => !current.some((existing) => existing.id === job.id))] : page);
            setHasMore(page.length === HISTORY_PAGE_SIZE);
        } catch {
            if (!controller.signal.aborted) setError('Copy history could not be loaded. Try again.');
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        if (!open) return;
        setHistory([]);
        setHasMore(false);
        void load();
        return () => abortRef.current?.abort();
    }, [open, load]);

    const retry = async (job: ProductFitmentCopyJob) => {
        const retried = await onRetry(job);
        if (retried) setHistory((current) => current.map((item) => item.id === job.id ? retried : item));
        else setError('The copy could not be restarted. Try again.');
        return retried;
    };
    // Polling keeps already loaded rows live without discarding older pages.
    const currentJobs = new Map(jobs.map((job) => [job.id, job]));
    return (
        <Modal open={open} onClose={onClose} title="Copy history" primaryAction={{ content: 'Done', onAction: onClose }}
            secondaryActions={hasMore && !error ? [{ content: 'Load older', loading, disabled: loading,
                onAction: () => { void load(history[history.length - 1]?.id); } }] : []}>
            <Modal.Section>
                <div className="pf-copy-history">
                    {error && <Banner tone="critical" title={error} action={{ content: 'Reload history', onAction: () => { void load(lastCursor.current); } }} />}
                    {!history.length && !loading && !error && <Text as="p" tone="subdued">No copy history for this product.</Text>}
                    {history.map((job) => <CopyActivityEntry key={job.id} job={currentJobs.get(job.id) ?? job} absoluteDate onRetry={retry} {...props} />)}
                    {loading && <div className="pf-copy-history__loading" role="status"><Spinner size="small" accessibilityLabel="Loading copy history" /><Text as="p">Loading copy history…</Text></div>}
                </div>
            </Modal.Section>
        </Modal>
    );
}
